import { Flex, Text, Image } from "rebass/styled-components";
import styled from "styled-components";
import { MdOutlineVideoLibrary } from "react-icons/md";

import { Button } from "MVVM/Components/Button";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { PlaylistClip } from "../../@types";
import Close from "../../../../../assets/images/floatingannouncement/close.svg";
import theme from "theme/themeThree";

const WatchLimitBlocker = ({
  onClose,
  playerName,
  clickedClip,
  playlistId,
  primaryColor = theme.colors.envy,
  secondaryColor = theme.colors.ice,
}: {
  onClose: () => void;
  playerName?: string;
  clickedClip?: PlaylistClip | null;
  playlistId: string;
  primaryColor?: string;
  secondaryColor?: string;
}) => {
  const { track } = useAnalyticsContext();

  const handleClose = () => {
    const platform =
      new URLSearchParams(window.location.search).get("platform") || "unknown";

    track("Button - Click", {
      location: "embeddable_playlist",
      locationIdentifier: "watch_limit_close",
      source: "website",
      platform,
      clipId: clickedClip?.shareId,
      playlistId: playlistId,
    });

    onClose();
  };

  const handleKeepWatching = () => {
    const platform =
      new URLSearchParams(window.location.search).get("platform") || "unknown";

    track("Button - Click", {
      location: "embeddable_playlist",
      locationIdentifier: "watch_limit_keep_watching",
      source: "website",
      platform,
      clipId: clickedClip?.shareId,
      playlistId: playlistId,
    });
    const ajs_aid = window.rudderanalytics?.getAnonymousId() || "";
    if (clickedClip && playlistId) {
      const clipUrl = `/clip/${clickedClip.shareId}?context=playlist&playlistId=${playlistId}&tab=recommended&utm_source=${platform}&ajs_aid=${ajs_aid}`;

      window.open(clipUrl, "_blank");
    } else {
      window.open(`https://allstar.gg?ajs_aid=${ajs_aid}`, "_blank");
    }
  };

  const getMainText = () => (
    <>
      Watch more of{" "}
      <PlayerName $primaryColor={primaryColor}>
        {playerName || "player"}'s{" "}
      </PlayerName>
      highlights.
    </>
  );

  return (
    <BlockerOverlay>
      <CloseIcon src={Close} onClick={handleClose} />
      <StyledPlayIcon $primaryColor={primaryColor} />
      <MainText>{getMainText()}</MainText>
      <SubText>
        For the best viewing experience, watch the rest of this playlist on
        Allstar.gg right now.
      </SubText>
      <StyledButton
        size="tiny"
        classification="roundedFilled"
        flex={true}
        onClick={handleKeepWatching}
        $primaryColor={primaryColor}
        $secondaryColor={secondaryColor}
      >
        Watch More
      </StyledButton>
    </BlockerOverlay>
  );
};

const BlockerOverlay = styled(Flex)`
  -webkit-backdrop-filter: blur(8px);
  align-items: center;
  backdrop-filter: blur(8px);
  border-radius: 8px;
  bottom: 0;
  flex-direction: column;
  justify-content: center;
  padding: 24px;
  position: absolute;
  text-align: center;
  z-index: 10;
`;

const MainText = styled(Text)`
  color: white;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 12px;

  @media (max-width: 502px) {
    font-size: 16px;
    margin-bottom: 5px;
  }

  @media (max-width: 425px) {
    font-size: 14px;
  }

  @media (max-width: 380px) {
    font-size: 12px;
  }

  @media (max-width: 360px) {
    margin-bottom: 5px;
  }
`;

const SubText = styled(Text)`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  max-width: 400px;

  @media (max-width: 502px) {
    font-size: 12px;
  }

  @media (max-width: 425px) {
    font-size: 11px;
  }

  @media (max-width: 380px) {
    font-size: 10px;
  }
`;

const CloseIcon = styled(Image)`
  cursor: pointer;
  height: 32px;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: 0.3s;
  width: 32px;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 502px) {
    width: 28px;
    height: 28px;
  }

  @media (max-width: 380px) {
    width: 24px;
    height: 24px;
  }
`;

const PlayerName = styled.span<{ $primaryColor: string }>`
  color: ${({ $primaryColor }) => $primaryColor};
`;

const StyledPlayIcon = styled(MdOutlineVideoLibrary)<{ $primaryColor: string }>`
  color: ${({ $primaryColor }) => $primaryColor};

  height: 48px;
  margin-bottom: 16px;
  width: 48px;

  @media (max-width: 502px) {
    height: 32px;
    margin-bottom: 5px;
    width: 32px;
  }

  @media (max-width: 425px) {
    height: 28px;
    margin-bottom: 8px;
    width: 28px;
  }

  @media (max-width: 380px) {
    height: 24px;
    margin-bottom: 6px;
    width: 24px;
  }

  @media (max-width: 360px) {
    display: none;
  }
`;

const StyledButton = styled(Button)<{
  $primaryColor: string;
  $secondaryColor: string;
}>`
  margin-top: 20px;
  background-color: ${({ $primaryColor }) => $primaryColor};
  color: ${({ $secondaryColor }) => $secondaryColor};

  @media (max-width: 502px) {
    font-size: 10px;
    height: 24px;
    line-height: 16px;
    margin-top: 5px;
    padding: 4px 8px;
  }

  @media (max-width: 380px) {
    font-size: 9px;
    height: 20px;
    line-height: 14px;
    margin-top: 8px;
    padding: 3px 6px;
  }

  @media (max-width: 360px) {
    font-size: 8px;
    height: 20px;
    line-height: 10px;
    margin-top: 5px;
    padding: 3px 6px;
  }
`;

export { WatchLimitBlocker };
